/** Site dashboard shell: loads the site, provides SiteContext, left sub-nav + <Outlet/>. Route /sites/:id/* */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Grid, Menu, Result, Select, Space, Spin, Typography } from 'antd';
import {
    DashboardOutlined, FileOutlined, EditOutlined, ShoppingOutlined, AppstoreOutlined, ProfileOutlined, TeamOutlined,
    TagOutlined, TagsOutlined, MessageOutlined, ReadOutlined, FormOutlined, GlobalOutlined, CreditCardOutlined, SettingOutlined, HistoryOutlined, ExportOutlined, ArrowLeftOutlined, PictureOutlined,
    LineChartOutlined, FileAddOutlined, ShoppingCartOutlined, DatabaseOutlined, GiftOutlined, StarOutlined, MailOutlined, UserAddOutlined, RollbackOutlined,
} from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { userPath } from '../../../shared/config/paths';
import { QUIET, sb, site as siteApi } from './api';
import { SiteContext, StatusTag, confirmLeave, editorPath, listOf, siteUrl, sitePath, useAntVars } from './common';

const { Text, Title } = Typography;

export const SITE_TABS = [
    { key: '', label: 'Overview', icon: <DashboardOutlined /> },
    { key: 'analytics', label: 'Analytics', icon: <LineChartOutlined /> },
    { key: 'pages', label: 'Pages', icon: <FileOutlined /> },
    { key: 'editor', label: 'Editor', icon: <EditOutlined />, editor: true },
    { group: 'Store' },
    { key: 'products', label: 'Products', icon: <ShoppingOutlined /> },
    { key: 'collections', label: 'Collections', icon: <AppstoreOutlined /> },
    { key: 'orders', label: 'Orders', icon: <ProfileOutlined /> },
    { key: 'returns', label: 'Returns', icon: <RollbackOutlined /> },
    { key: 'customers', label: 'Customers', icon: <TeamOutlined /> },
    { key: 'draft-orders', label: 'Draft orders', icon: <FileAddOutlined /> },
    { key: 'abandoned', label: 'Abandoned carts', icon: <ShoppingCartOutlined /> },
    { key: 'inventory', label: 'Inventory', icon: <DatabaseOutlined /> },
    { key: 'discounts', label: 'Discounts', icon: <TagOutlined /> },
    { key: 'gift-cards', label: 'Gift cards', icon: <GiftOutlined /> },
    { key: 'reviews', label: 'Reviews', icon: <StarOutlined /> },
    { key: 'payments', label: 'Payments', icon: <CreditCardOutlined /> },
    { key: 'marketing', label: 'Marketing', icon: <MailOutlined /> },
    { group: 'Content' },
    { key: 'blog', label: 'Blog', icon: <ReadOutlined /> },
    { key: 'comments', label: 'Comments', icon: <MessageOutlined /> },
    { key: 'blog-terms', label: 'Categories', icon: <TagsOutlined /> },
    { key: 'blog-authors', label: 'Authors', icon: <TeamOutlined /> },
    { key: 'forms', label: 'Forms', icon: <FormOutlined /> },
    { key: 'collections-cms', label: 'Collections', icon: <AppstoreOutlined /> },
    { key: 'bookings', label: 'Bookings', icon: <HistoryOutlined /> },
    { key: 'media', label: 'Media', icon: <PictureOutlined /> },
    { group: 'Site' },
    { key: 'domains', label: 'Domains', icon: <GlobalOutlined /> },
    { key: 'staff', label: 'Staff', icon: <UserAddOutlined /> },
    { key: 'settings', label: 'Settings', icon: <SettingOutlined /> },
    { key: 'versions', label: 'Versions', icon: <HistoryOutlined /> },
];

// Staff roles (server-side areas in SiteStaffService): which menu entries each role can open.
const TAB_AREA = {
    '': 'site', pages: 'content', editor: 'content', blog: 'content', comments: 'content', 'blog-terms': 'content', 'blog-authors': 'content', forms: 'content', 'collections-cms': 'content', bookings: 'content', media: 'content', versions: 'content',
    products: 'products', collections: 'products', inventory: 'products', orders: 'orders', returns: 'orders', 'draft-orders': 'orders',
    customers: 'customers', abandoned: 'abandoned', discounts: 'marketing', reviews: 'marketing', marketing: 'marketing',
    'gift-cards': 'gift_cards', analytics: 'analytics', payments: 'payments', staff: 'staff', domains: 'settings', settings: 'settings',
};
const ROLE_AREAS = {
    admin: ['site', 'content', 'settings', 'products', 'orders', 'customers', 'gift_cards', 'marketing', 'abandoned', 'analytics'],
    editor: ['site', 'content'],
    orders: ['site', 'orders', 'customers', 'gift_cards', 'abandoned', 'analytics'],
    marketing: ['site', 'marketing', 'gift_cards', 'abandoned', 'customers', 'analytics'],
};
/** role = null for the site owner (everything) */
export const canOpenTab = (role, key) => !role || role === 'owner' || (ROLE_AREAS[role] || []).includes(TAB_AREA[key] ?? 'other');

const SiteLayout = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const screens = Grid.useBreakpoint();
    const [site, setSite] = useState(null);
    const [shop, setShop] = useState(null);
    const [err, setErr] = useState(null);
    const [role, setRole] = useState(null); // staff role on someone else's site; null = owner
    useAntVars();

    const api = useMemo(() => siteApi(id), [id]);
    const reload = useCallback(async () => {
        try {
            const s = await api.get('');
            setSite(s?.site || s);
            setErr(null);
            return s;
        } catch (e) {
            setErr(e);
            return null;
        }
    }, [api]);
    const reloadShop = useCallback(async () => {
        try {
            const s = await api.get('/shop-settings', null, QUIET);
            setShop(s?.settings || s || {});
        } catch (_) { setShop({}); }
    }, [api]);
    useEffect(() => { reload(); reloadShop(); }, [reload, reloadShop]);
    useEffect(() => {
        let live = true;
        sb.get('/shared', null, QUIET).then((d) => {
            const mine = listOf(d?.sites).find((x) => String(x.id) === String(id));
            if (live) setRole(mine?.staff_role || null);
        }).catch(() => {});
        return () => { live = false; };
    }, [id]);

    const base = sitePath(id);
    const rest = location.pathname.replace(/\/+$/, '').split(`/sites/${id}`)[1] || '';
    const current = rest.split('/')[1] || '';

    const ctx = useMemo(() => ({ site, siteId: id, api, reload, setSite, shop, reloadShop, currency: shop?.currency || 'USD', role }), [site, id, api, reload, shop, reloadShop, role]);

    if (err && !site) {
        return <Result status={err.status === 404 ? '404' : 'error'} title={err.status === 404 ? t('Site not found') : (err.error || t('Failed to load'))}
            extra={<Button onClick={() => navigate(userPath('/sites'))}>{t('All sites')}</Button>} />;
    }
    if (!site) return <div style={{ padding: 80, textAlign: 'center' }}><Spin /></div>;

    const go = (key) => confirmLeave(() => {
        const tab = SITE_TABS.find((x) => x.key === key);
        if (tab?.editor) navigate(editorPath(id));
        else navigate(key ? `${base}/${key}` : base);
    });
    // Store entries only for shops; their routes keep working. A site without a kind yet keeps the full menu.
    const hideStore = site.kind && site.kind !== 'shop';
    const storeFrom = SITE_TABS.findIndex((x) => x.group === 'Store');
    const storeTo = SITE_TABS.findIndex((x) => x.group === 'Content');
    const tabs = SITE_TABS.filter((x, i) => !(hideStore && i >= storeFrom && i < storeTo)).filter((x) => x.group || canOpenTab(role, x.key));
    const items = [];
    let group = null;
    tabs.forEach((x) => {
        if (x.group) { group = { type: 'group', key: `g-${x.group}`, label: t(x.group), children: [] }; items.push(group); return; }
        const it = { key: x.key || 'overview', icon: x.icon, label: t(x.label, { context: 'shop' }) };
        if (group) group.children.push(it); else items.push(it);
    });
    for (let i = items.length - 1; i >= 0; i -= 1) if (items[i].type === 'group' && !items[i].children.length) items.splice(i, 1);
    const url = siteUrl(site);

    return (
        <SiteContext.Provider value={ctx}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => confirmLeave(() => navigate(userPath('/sites')))} />
                <Title level={4} style={{ margin: 0 }}>{site.name}</Title>
                <StatusTag value={site.status} />
                {url && <a href={url} target="_blank" rel="noopener noreferrer"><Text type="secondary">{url.replace(/^https?:\/\//, '')} <ExportOutlined /></Text></a>}
                <div style={{ flex: 1 }} />
                <Space>
                    {canOpenTab(role, 'editor') && <Button type="primary" icon={<EditOutlined />} onClick={() => confirmLeave(() => navigate(editorPath(id)))}>{t('Edit site')}</Button>}
                </Space>
            </div>
            {site.status === 'suspended' && <Alert type="error" showIcon style={{ marginBottom: 16 }} message={t('This site is suspended.')} description={site.suspend_reason === 'billing' ? t('Renew the plan to bring it back online.') : undefined} />}
            {/* the Outlet keeps its place in the tree: crossing the breakpoint must not remount the page (unsaved editors) */}
            <div style={{ display: screens.md ? 'flex' : 'block', gap: 16, alignItems: 'flex-start' }}>
                {screens.md ? (
                    <div style={{ width: 200, flex: 'none', position: 'sticky', top: 72 }}>
                        <Menu mode="inline" selectedKeys={[current || 'overview']} items={items} onClick={({ key }) => go(key === 'overview' ? '' : key)}
                            style={{ borderInlineEnd: 0, background: 'transparent' }} />
                    </div>
                ) : (
                    <Select value={current || ''} onChange={go} style={{ width: '100%', marginBottom: 12 }}
                        options={tabs.filter((x) => !x.group).map((x) => ({ value: x.key, label: t(x.label, { context: 'shop' }) }))} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}><Outlet /></div>
            </div>
        </SiteContext.Provider>
    );
};

export default SiteLayout;
